import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BaseLogo, getLogoOrgId } from "../core/models/entities/BaseLogo";
import { enviroment } from "../enviroment";
import { useApiClient } from "../data/clients/ApiClient";
import { mapToLogo } from "../data/models/mappers/mapLogo";
import {
  logoDetailKey,
  patchLogoRowsFromDetail,
} from "../data/repositories/logoDetailCache";
import {
  isLogoProductPending,
  isLogoUploadPending,
  isProductPendingStale,
  isUploadRejected,
} from "../utils/functions/productPending";
import { productPendingPollDelay } from "../utils/functions/productPendingPoll";

// Flags REACT_APP_PRODUCT_PENDING_ENABLED / REACT_APP_CLONE_INSTANT_ENABLED -
// build-time constant, so the export at the bottom is fixed for the lifetime
// of the bundle and hook order never varies at runtime (same idiom as
// useEditorUploadPendingPoll). Callers only ever have a pending row under
// one of the two flags, so with both off nothing can be scheduled.
const listPollEnabled =
  enviroment.productPendingEnabled || enviroment.cloneInstantEnabled;

/**
 * Above this many pending rows on the page ONE list refetch (GetAllLogos)
 * per tick replaces the per-row GetLogo calls: the list call IS the batch
 * endpoint (there is no GET logos/pending-state?ids=…), and N GetLogo of
 * ~1.4-1.8 s each cost more than one list call from N = 4 on.
 */
export const LIST_PENDING_POLL_MAX_DETAIL_ROWS = 3;

export interface LogoListPendingPollArgs {
  /** The rows the list shows right now (page.results). */
  rows: readonly BaseLogo[] | undefined;
  /**
   * The list's settled state (success, not fetching, not previous data) -
   * the same gate as useStorageFreshness, so rows kept on screen from the
   * previous query (keepPreviousData) are never polled.
   */
  settled: boolean;
  /** The list's own refetch (useAllLogos().refetch) - used above the cap. */
  refetchList: () => unknown;
  /**
   * Rows another view is ALREADY polling on the same detail key - the row
   * open in the popup (LogoDetailsWrapper's useProductPendingPoll, rendered
   * inside the list screen so both hooks are live at once). Those rows are
   * left to that view: skipped in the tick, never fetched from here. The
   * popup's own landing is copied into the list row by the wrapper's
   * patchLogoRowsFromDetail effect, so the row still clears. Not part of
   * the budget bookkeeping: opening/closing the popup neither restarts the
   * budget nor counts as a new id.
   */
  excludeIds?: readonly number[];
}

/**
 * The rows still worth a re-read: pending under an ENABLED variant and
 * younger than the 10-minute stale line. A stale row already reads "Prices
 * delayed" / "Logo preparation delayed" (worker poisoned, ops mail sent) and
 * is not polled - the badge changes by itself when the sweep lands it and
 * the user opens or reloads the page. A REJECTED upload (flag
 * REACT_APP_UPLOAD_PENDING_ENABLED, uploadFailedReason set) keeps its stamp
 * by design until the user removes the row - nothing will ever land on it,
 * so it is not polled either. Pure; `now` injectable for tests.
 */
export function pendingListCandidates(
  rows: readonly BaseLogo[] | undefined,
  now: number = Date.now()
): BaseLogo[] {
  return (rows ?? []).filter((row) => {
    if (!row || typeof row.id !== "number" || row.id <= 0) return false;
    const product =
      isLogoProductPending(row) &&
      !isProductPendingStale(row.productPendingSinceUtc, now);
    const upload =
      isLogoUploadPending(row) &&
      !isUploadRejected(row) &&
      !isProductPendingStale(row.uploadPendingSinceUtc, now);
    return product || upload;
  });
}

type LogoListPendingPoll = (args: LogoListPendingPollArgs) => void;

/**
 * Bounded re-read for the "Prices on their way" / "Preparing logo…" rows of
 * the logo LIST (flags REACT_APP_PRODUCT_PENDING_ENABLED /
 * REACT_APP_CLONE_INSTANT_ENABLED).
 *
 * Why: every other pending re-read (popup, editor, cart) lands in the DETAIL
 * entry of one logo, and the list entry is only rewritten by a query change
 * or the return-to-list refetch - which races the API worker (the list is
 * refetched before the worker has cleared the stamp) - so a row stayed on
 * "Prices on their way" until F5 (prod 675889, 11/9). This hook is the one
 * re-read that works on the rows themselves.
 *
 * Schedule: the shared 3/8/15 s plan (productPendingPollDelay), at most
 * MAX_PRODUCT_PENDING_POLLS ticks per view; the budget restarts only when a
 * NEW pending id appears on the page (same rule as useProductPendingPoll -
 * an id that lands and leaves keeps the running budget), and the loop stops
 * the moment no candidate is left, the view unmounts or the budget is spent
 * (the badge then simply stays; a new view / query = a new budget). Only
 * armed while the list is `settled`; the candidate set and the budget are
 * still tracked while it is not, so a refetch of the very same rows never
 * hands them a fresh budget.
 *
 * Per tick the candidates are re-evaluated on the CURRENT rows, minus the
 * rows in `excludeIds` (the row open in the popup polls itself - one poller
 * per detail key, so the popup + list never spend 6 GetLogo on one logo);
 * the cap below is judged on what is left:
 *  - N <= LIST_PENDING_POLL_MAX_DETAIL_ROWS: one GetLogo per row through
 *    queryClient.fetchQuery on the row's own detail key (["logos", actAs,
 *    id]) - the REAL detail entry, never a stub, so a popup opened next finds
 *    a cached GetLogo (0 extra calls) - and patchLogoRowsFromDetail copies
 *    the landed state into the list rows (0 calls). A fetch cancelled by a
 *    mutation's cancelInFlightDetailFetch (same key) is swallowed: the
 *    mutation's own seed wins, the next tick judges the new rows.
 *  - N > cap: ONE refetchList (GetAllLogos) instead of N GetLogo.
 *
 * Call budget per list view with pending rows: <= 3 ticks x min(N, 3)
 * GetLogo (N <= 3) or <= 3 GetAllLogos (N > 3); 0 when no row is pending or
 * every pending row is older than 10 minutes; 0 from here for the row open
 * in the popup (<= 3 from the popup's own poll); typically 1 GetLogo at 3 s
 * (worker p50 < 4 s). Per night: 0. Flag off (both): no hook body at all.
 */
function useLogoListPendingPollEnabled({
  rows,
  settled,
  refetchList,
  excludeIds,
}: LogoListPendingPollArgs): void {
  const queryClient = useQueryClient();
  const apiClient = useApiClient();
  const attempt = useRef(0);
  const [tick, setTick] = useState(0);

  // The timer fires later: it reads the rows, the excluded ids, the client
  // and the refetch of THAT moment (a patch or a refetch may have replaced
  // what the list held when the timer was armed; the popup may have opened
  // or closed since).
  const latestRef = useRef({ rows, refetchList, apiClient, excludeIds });
  latestRef.current = { rows, refetchList, apiClient, excludeIds };

  // Tracked on the rows as shown - settled or not - so a refetch of the same
  // rows (settled false -> true) is not a "new" set. Sorted so the order of
  // the page never counts as a change.
  const idsSignature = pendingListCandidates(rows)
    .map((row) => row.id)
    .sort((a, b) => a - b)
    .join(",");

  // The budget restarts only when an id we have NOT been polling appears; an
  // id that leaves (landed, or paged away) keeps the running count.
  const polledIds = useRef<Set<number>>(new Set());
  useEffect(() => {
    const ids = idsSignature === "" ? [] : idsSignature.split(",").map(Number);
    const hasNewId = ids.some((id) => !polledIds.current.has(id));
    polledIds.current = new Set(ids);
    if (hasNewId) attempt.current = 0;
  }, [idsSignature]);

  useEffect(() => {
    if (!settled || idsSignature === "") return;
    const delay = productPendingPollDelay(
      attempt.current,
      enviroment.productPendingPollMs
    );
    if (delay === undefined) return;
    const timer = setTimeout(() => {
      attempt.current += 1;
      const current = latestRef.current;
      // The excluded rows (open in the popup) are dropped BEFORE the cap is
      // judged and before any fetch - but not from the budget bookkeeping
      // above, so a popup opened and closed on the same row hands out no
      // fresh budget. A tick left with nothing to read still counts: the
      // schedule is the view's, not the row's.
      const excluded = new Set(current.excludeIds ?? []);
      const candidates = pendingListCandidates(current.rows).filter(
        (row) => !excluded.has(row.id)
      );
      if (candidates.length > LIST_PENDING_POLL_MAX_DETAIL_ROWS) {
        void Promise.resolve()
          .then(() => current.refetchList())
          .catch(() => undefined);
      } else {
        candidates.forEach((row) => {
          void queryClient
            .fetchQuery({
              queryKey: logoDetailKey(row.id),
              queryFn: () =>
                current.apiClient
                  .getLogo(getLogoOrgId(row), row.id)
                  .then(mapToLogo),
              staleTime: 0,
            })
            .then((detail) => {
              patchLogoRowsFromDetail(queryClient, detail);
            })
            .catch(() => undefined);
        });
      }
      // Re-armed at once (not after the answer): the schedule stays 3/8/15 s
      // from the tick whatever the round trip, and react-query dedupes a
      // fetch still in flight on the same key into one call.
      setTick((t) => t + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [settled, idsSignature, tick, queryClient]);
}

// Flag off: no hook body at all (no query-client read, no api client, no
// timer) - the call site keeps one hook call either way.
function useLogoListPendingPollDisabled(): void {}

export const useLogoListPendingPoll: LogoListPendingPoll = listPollEnabled
  ? useLogoListPendingPollEnabled
  : useLogoListPendingPollDisabled;
